import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Site } from '@services/api'
import { sitesApi } from '@services/api'

// 'site' = one property (spoke); 'brand' = you/the hub, account-level across all sites
export type Scope = 'site' | 'brand'

const SITE_SCOPE_ROUTES = ['/dashboard', '/tracking', '/submissions', '/backlinks', '/analytics']
const BRAND_SCOPE_ROUTES = ['/content', '/brand', '/github-seo']

// Keeps the sidebar scope toggle in sync when a user deep-links to a route
// instead of using the toggle (e.g. a bookmark or browser back/forward).
export function scopeForPath(pathname: string): Scope | null {
  if (SITE_SCOPE_ROUTES.some((r) => pathname.startsWith(r))) return 'site'
  if (BRAND_SCOPE_ROUTES.some((r) => pathname.startsWith(r))) return 'brand'
  return null
}

type SiteStore = {
  sites: Site[]
  activeSiteId: string | null
  scope: Scope
  isLoading: boolean
  fetchSites: () => Promise<void>
  setActiveSiteId: (id: string) => void
  setScope: (scope: Scope) => void
}

export const useSiteStore = create<SiteStore>()(
  persist(
    (set, get) => ({
      sites: [],
      activeSiteId: null,
      scope: 'site',
      isLoading: false,

      fetchSites: async () => {
        set({ isLoading: true })
        try {
          const sites = await sitesApi.getAll()
          const { activeSiteId } = get()
          // Keep existing active site if still valid, otherwise default to first
          const validId = sites.find((s) => s.id === activeSiteId)
            ? activeSiteId
            : (sites[0]?.id ?? null)
          set({ sites, activeSiteId: validId, isLoading: false })
        } catch {
          set({ isLoading: false })
        }
      },

      setActiveSiteId: (id) => set({ activeSiteId: id }),

      setScope: (scope) => set({ scope }),
    }),
    {
      name: 'seo-booster-site',
      // Only persist the active selection + scope — site list is always fetched fresh
      partialize: (s) => ({ activeSiteId: s.activeSiteId, scope: s.scope }),
    }
  )
)
